import { useRef, useState } from 'react'
import { Stack, TextField } from '@fluentui/react'

import { SpeechToTextArgs } from '../../../../hooks/useSpeech'
import { SpeechToTextStatusesEnum } from '../../../../constants/speech'

import Send from '../../../../assets/Send.svg'
import Microphone from '../../../../assets/microphone.svg'
import MicrophoneLoading from '../../../../assets/microphoneLoading.svg'
import MicrophoneStop from '../../../../assets/microphoneStop.svg'

import styles from './QuestionInput.module.css'
import { useIsMobile } from '../../../../hooks/useIsMobile';

interface Props {
  onSend: (question: string, id?: string) => void
  disabled: boolean
  isSpeechEnabled: boolean
  speechToTextStatus: SpeechToTextStatusesEnum,
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string,
  isGeneratingAnswer?:boolean,
  onStopGenerating?: () => void,
  emitSpeechToText: (opts: SpeechToTextArgs) => void,
  emitTerminateSpeechToText: () => void
}

export const QuestionInput = ({
  onSend,
  disabled,
  isSpeechEnabled,
  speechToTextStatus,
  placeholder,
  clearOnSend,
  conversationId,
  isGeneratingAnswer,
  onStopGenerating,
  emitSpeechToText,
  emitTerminateSpeechToText
}: Props) => {

  const isMobile = useIsMobile(900);
  
  const [question, setQuestionSync] = useState<string>('')
  const [isListeningMode, setIsListeningMode] = useState<boolean>(false)
  const questionRef = useRef(question)
  const setQuestion = (value: string) => {
    setQuestionSync(value)
    questionRef.current = value
  }
  const isSpeechToTextEnabled = speechToTextStatus == SpeechToTextStatusesEnum.Active

  const sendQuestion = () => {
    const text = questionRef.current

    if (disabled || !text.trim()) return

    if (conversationId) {
      onSend(text, conversationId)
    } else {
      onSend(text)
    }

    if (clearOnSend) setQuestion('')
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
  }

  const onSpeechRecognitionSwitcherToggle = () => {
    if (isGeneratingAnswer && onStopGenerating) {
      onStopGenerating()
      setIsListeningMode(false)
      return
    }
    if (disabled) return

    if (isSpeechToTextEnabled) {
      emitTerminateSpeechToText()
      setQuestion('')
      setIsListeningMode(false)
    } else {
      emitSpeechToText({
        onInit: () => setQuestion(''),
        onRecognizing: (resultText?: string) => {
          const text = String(resultText || '');
          setQuestion(text.substring(0, 1000));
      },
        onRecognized: () => sendQuestion()
      })
      setIsListeningMode(true)
    }
  }

  const sendQuestionDisabled = isGeneratingAnswer || disabled || !question.trim()

  const speechToTextIcon = () => {
    if(isGeneratingAnswer && isListeningMode) {
      return <img src={MicrophoneStop} alt="microphone" className={styles.iconButton} />
    }
    switch (speechToTextStatus) {
      case SpeechToTextStatusesEnum.Initializing:
        return <img src={MicrophoneLoading} alt="microphone" className={styles.iconButtonLoading} />
      case SpeechToTextStatusesEnum.Active:
          return (
          <div className={styles.activeMicWrapper}>
            <img src={Microphone} alt="microphone" className={styles.iconButton} />
            <div className={styles.wave}></div>
          </div>)
      case SpeechToTextStatusesEnum.Inactive:
      default:
          return <img src={Microphone} alt="microphone" className={styles.iconButton} />
    }
  }

  return (
    <Stack horizontal className={styles.questionInputContainer}>
      {isSpeechEnabled && <button   
          className={styles.micWrapper}     
          aria-label="Speech input button"
          onClick={onSpeechRecognitionSwitcherToggle}
      >
        {speechToTextIcon()}
      </button>}
      <div className={styles.questionInputFieldWrapper}>
        <TextField
          className={styles.questionInputTextArea}
          placeholder={isSpeechToTextEnabled ? "Listening..." : placeholder}
          multiline
          resizable={false}
          autoAdjustHeight
          borderless
          value={question}
          onChange={onQuestionChange}
          onKeyDown={onEnterPress}
          maxLength={1000}
        />
        <div className={styles.privacyDisclaimer}>
          By using Leora, you agree to our{' '}
          <a href="https://leobit.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          .
        </div>
      </div>

      <button
        className={styles.questionInputSendButtonContainer}
        aria-label="Ask question button"
        onClick={sendQuestion}
        disabled={sendQuestionDisabled}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
        {isMobile && <img src={Send} className={styles.questionInputSendButton} alt="Send Button" />}
      </button>
    </Stack>
  )
}
